import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import axios from 'axios';
import { Ship, Calendar, MapPin, Users, Anchor, TrendingDown } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MATERIAL_COLORS = {
  plastica_monouso: '#3B82F6',
  mozziconi_cotton: '#F97316',
  attrezzatura_pesca: '#EF4444',
  polistirolo: '#FBBF24',
  microplastiche: '#8B5CF6',
  vetro_ceramica: '#06B6D4',
  metalli: '#EC4899',
  rifiuti_sanitari: '#14B8A6',
  ingombranti: '#22C55E',
  altro: '#78716C',
  generic: '#94A3B8'
};

const MATERIAL_LABELS = {
  plastica_monouso: 'Plastica Monouso',
  mozziconi_cotton: 'Mozziconi',
  attrezzatura_pesca: 'Attrezz. Pesca',
  polistirolo: 'Polistirolo',
  microplastiche: 'Microplastiche',
  vetro_ceramica: 'Vetro/Ceramica',
  metalli: 'Metalli',
  rifiuti_sanitari: 'Rifiuti Sanitari',
  ingombranti: 'Ingombranti',
  altro: 'Altro',
  generic: 'Generico'
};

const FishingForLitterSection = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_kg: 0, total_collections: 0, total_participants: 0 });

  const fetchSeaCollections = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/collections/sea`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollections(response.data);
      
      // Calcola statistiche
      const total_kg = response.data.reduce((sum, c) => sum + (c.kg_collected || 0), 0);
      const total_participants = response.data.reduce((sum, c) => sum + (c.participants_count || 1), 0);
      setStats({
        total_kg: total_kg,
        total_collections: response.data.length,
        total_participants: total_participants
      });
    } catch (error) {
      console.error('Error fetching sea collections:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSeaCollections();
  }, [fetchSeaCollections]);

  // Prepara dati per il grafico a torta
  const pieData = useCallback(() => {
    const categoryTotals = {};
    
    collections.forEach(col => {
      if (col.categories) {
        Object.entries(col.categories).forEach(([key, value]) => {
          if (value > 0) {
            categoryTotals[key] = (categoryTotals[key] || 0) + value;
          }
        });
      }
    });

    return Object.entries(categoryTotals)
      .filter(([_, value]) => value > 0)
      .map(([key, value]) => ({
        name: MATERIAL_LABELS[key] || key,
        value: parseFloat(value.toFixed(2)),
        color: MATERIAL_COLORS[key] || '#94A3B8'
      }))
      .sort((a, b) => b.value - a.value);
  }, [collections]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  const chartData = pieData();

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="w-20 h-20 rounded-full bg-orange-500/20 flex items-center justify-center">
            <Ship className="w-10 h-10 text-orange-400" />
          </div>
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
          FISHING FOR LITTER
        </h2>
        <p className="text-slate-400 mt-2"><TranslatedText text="Raccolte in mare dai pescherecci" /></p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-slate-900/60 backdrop-blur-xl border border-orange-500/30 rounded-xl p-4 text-center">
          <Anchor className="w-6 h-6 text-orange-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{stats.total_collections}</p>
          <p className="text-xs text-slate-400"><TranslatedText text="Raccolte" /></p>
        </div>
        <div className="bg-slate-900/60 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-4 text-center">
          <TrendingDown className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{stats.total_kg.toFixed(1)}</p>
          <p className="text-xs text-slate-400"><TranslatedText text="Kg Totali" /></p>
        </div>
        <div className="bg-slate-900/60 backdrop-blur-xl border border-emerald-500/30 rounded-xl p-4 text-center">
          <Users className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{stats.total_participants}</p>
          <p className="text-xs text-slate-400"><TranslatedText text="Partecipanti" /></p>
        </div>
      </div>

      {/* Grafico a Torta */}
      {chartData.length > 0 && (
        <div className="bg-slate-900/60 backdrop-blur-xl border border-orange-500/30 rounded-2xl p-6 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4 text-center">Materiali Raccolti in Mare</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, value }) => `${value} kg`}
                  labelLine={false}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Legend 
                  formatter={(value) => <span className="text-slate-300 text-xs">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Lista Raccolte */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-white mb-3">Storico Raccolte in Mare</h3>
        
        {collections.length === 0 ? (
          <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-8 text-center">
            <Ship className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">Nessuna raccolta in mare registrata</p>
            <p className="text-slate-500 text-sm mt-1">Le raccolte con "Raccolta in mare" attivo appariranno qui</p>
          </div>
        ) : (
          collections.map((col) => (
            <div key={col.id} className="bg-slate-900/60 border border-orange-500/20 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                    <Ship className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{col.volunteer_name || 'Anonimo'}</span>
                      {col.organization_name && (
                        <span className="text-orange-400 text-sm">({col.organization_name})</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(col.timestamp)}
                      </span>
                      {col.participants_count > 1 && (
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {col.participants_count} persone
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-orange-400">{col.kg_collected} kg</p>
                  <p className="text-xs text-slate-500">
                    {(col.kg_collected * 0.15).toFixed(2)} kg CO₂eq
                  </p>
                </div>
              </div>
              
              {col.place && (
                <div className="mt-2 flex items-center gap-1 text-sm text-slate-400">
                  <MapPin className="w-3 h-3" />
                  <span>{col.place}</span>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FishingForLitterSection;
